// =============================================================================
// SHANIZ ADMIN — SHARED CHART THEME
// One curated, colorblind-safe categorical palette + theme-aware axis/grid/
// tooltip tokens for every recharts chart. Adapts to light/dark automatically.
//
// The categorical palette was validated with the dataviz palette validator
// (six checks) against BOTH the dark (#1A1A24) and light (#ffffff) chart
// surfaces: CVD-safe (worst adjacent ΔE 23.8), contrast ≥ 3:1 on both, chroma
// floor met, lightness band met in both modes. Do NOT hand-edit these hexes or
// cycle past the 8th slot — a 9th series folds into "Other" (SERIES_OTHER).
// =============================================================================

import { Theme } from '@mui/material';

/** Fixed-order categorical series palette. Assign by entity, never by rank. */
export const CHART_SERIES = [
  '#B8860B', // gold
  '#2563EB', // blue
  '#DB2777', // pink
  '#16A34A', // green
  '#7C3AED', // purple
  '#EA580C', // orange
  '#0891B2', // cyan
  '#DC2626', // red
] as const;

/** Neutral for the "Other" bucket / 9th+ series. */
export const SERIES_OTHER = '#94A3B8';

/** Color for series index i (folds to Other beyond the palette). */
export function seriesColor(i: number): string {
  return i < CHART_SERIES.length ? CHART_SERIES[i] : SERIES_OTHER;
}

export interface ChartTokens {
  grid: string;       // CartesianGrid stroke — recessive
  axisTick: string;   // axis tick label fill — secondary ink
  axisLine: string;   // axis line stroke
  tooltipBg: string;  // tooltip surface
  tooltipBorder: string;
  tooltipText: string;
}

/** Theme-aware chart chrome (grid/axis/tooltip) derived from the MUI theme. */
export function chartTokens(theme: Theme): ChartTokens {
  const p = theme.palette;
  return {
    grid: p.divider,
    axisTick: p.text.secondary,
    axisLine: p.divider,
    tooltipBg: p.background.paper,
    tooltipBorder: p.divider,
    tooltipText: p.text.primary,
  };
}

/** Ready-to-spread recharts <Tooltip> styling (surface + ink tokens, never a series color). */
export function tooltipProps(theme: Theme) {
  const t = chartTokens(theme);
  return {
    contentStyle: {
      background: t.tooltipBg,
      border: `1px solid ${t.tooltipBorder}`,
      borderRadius: 12,
      color: t.tooltipText,
      fontSize: 12,
      boxShadow:
        theme.palette.mode === 'dark'
          ? '0 6px 24px rgba(0,0,0,0.5)'
          : '0 4px 16px rgba(15,23,42,0.12)',
    },
    labelStyle: { color: t.tooltipText, fontWeight: 700 },
    itemStyle: { color: t.tooltipText },
    cursor: { fill: theme.palette.action.hover },
  };
}
